import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, Home, Plus, Loader2, AlertCircle } from 'lucide-react';
import { api } from '../services/api';
import { trackEvent } from '../services/analytics';
import type { Property } from './PropertyManager';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called when the user picks a property (existing or freshly created). */
  onPick: (property: Property) => void;
}

type Item =
  | { kind: 'property'; property: Property }
  | { kind: 'new'; label: string };

const timeAgo = (dt: string | null): string => {
  if (!dt) return 'Geen renders';
  const diff = Date.now() - new Date(dt).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Zojuist';
  if (mins < 60) return `${mins} min geleden`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} uur geleden`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} dagen geleden`;
  return new Date(dt).toLocaleDateString('nl-NL', { day: '2-digit', month: 'short' });
};

/**
 * Highlight matching substring in the address.
 * Case-insensitive, first match only.
 */
const Highlighted: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-accent/20 text-ink rounded px-0.5">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
};

export const StartStagingModal: React.FC<Props> = ({ open, onClose, onPick }) => {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loadingProps, setLoadingProps] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset + fetch on open
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
    setError(null);
    setTimeout(() => inputRef.current?.focus(), 20);

    setLoadingProps(true);
    api.get<Property[]>('/api/properties')
      .then(data => setProperties(Array.isArray(data) ? data : []))
      .catch(() => setProperties([]))
      .finally(() => setLoadingProps(false));
  }, [open]);

  // Filter + sort: recent first, exact-prefix matches before substring matches
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? properties.filter(p =>
          (p.address + ' ' + (p.city ?? '')).toLowerCase().includes(q)
        )
      : [...properties];
    list.sort((a, b) => {
      // Recently used first
      const aDate = a.last_render_at || a.created_at;
      const bDate = b.last_render_at || b.created_at;
      return (bDate || '').localeCompare(aDate || '');
    });
    return list.slice(0, 8);
  }, [query, properties]);

  // Items in render order: matches, then "new" affordance
  const items = useMemo<Item[]>(() => {
    const result: Item[] = matches.map(p => ({ kind: 'property' as const, property: p }));
    const q = query.trim();
    // Show "new" affordance always, but with typed value if user is typing.
    // If query exactly matches a property address (case-insensitive), don't show "new".
    const exactExisting = q && properties.some(p => p.address.toLowerCase() === q.toLowerCase());
    if (!exactExisting) {
      result.push({ kind: 'new' as const, label: q || 'Nieuwe woning aanmaken' });
    }
    return result;
  }, [matches, query, properties]);

  // Clamp active when items change
  useEffect(() => {
    if (active >= items.length) setActive(Math.max(0, items.length - 1));
  }, [items, active]);

  // Esc close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const pickExisting = (property: Property) => {
    trackEvent('start_staging_pick_existing', { propertyId: property.id });
    onPick(property);
    onClose();
  };

  const createAndPick = async (rawAddress: string) => {
    const address = rawAddress.trim();
    if (!address) {
      setError('Vul een adres in.');
      inputRef.current?.focus();
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const created = await api.post<Property>('/api/properties', { address });
      trackEvent('start_staging_create_new', { propertyId: created.id });
      onPick(created);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kon woning niet aanmaken. Probeer opnieuw.');
    } finally {
      setCreating(false);
    }
  };

  const runItem = (item: Item) => {
    if (item.kind === 'property') pickExisting(item.property);
    else createAndPick(query);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(a => Math.min(items.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(0, a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[active];
      if (item) runItem(item);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="start-staging-title"
      className="fixed inset-0 z-50 flex items-end md:items-start md:justify-center md:pt-32 bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        // Click on backdrop closes
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white w-full md:max-w-xl md:mx-4 rounded-t-3xl md:rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] md:max-h-[80vh] flex flex-col">
        {/* Drag handle (mobile) */}
        <div className="md:hidden pt-2 flex justify-center">
          <div className="w-10 h-1 rounded-full bg-ink/15" />
        </div>

        {/* Header */}
        <div className="px-6 pt-5 md:pt-6 pb-4 border-b border-black/8">
          <div className="flex items-center justify-between">
            <h2 id="start-staging-title" className="text-lg font-bold text-ink">Start staging</h2>
            <button
              type="button"
              onClick={onClose}
              className="md:hidden text-sm text-ink/55"
            >
              Annuleer
            </button>
          </div>
          <p className="text-xs text-ink/55 mt-1">Begin met typen om een woning te kiezen of een nieuwe aan te maken.</p>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-black/8">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActive(0); }}
              onKeyDown={onKeyDown}
              placeholder="Zoek of typ een nieuw adres…"
              disabled={creating}
              className="w-full pl-10 pr-3 py-3 rounded-xl border border-black/15 text-sm focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:opacity-60"
            />
          </div>
          {error && (
            <div className="mt-3 flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="overflow-y-auto flex-1">
          {loadingProps ? (
            <div className="py-10 text-center text-sm text-ink/50">
              <Loader2 size={16} className="inline animate-spin mr-2" /> Woningen laden…
            </div>
          ) : (
            <>
              {matches.length > 0 && (
                <p className="px-6 pt-4 pb-2 text-[10.5px] font-semibold uppercase tracking-[0.11em] text-ink/45">
                  {query.trim() ? `${matches.length} resultaat${matches.length === 1 ? '' : 'en'}` : 'Recent'}
                </p>
              )}
              {query.trim() && matches.length === 0 && (
                <div className="py-8 text-center">
                  <p className="text-xs text-ink/45">Geen woning gevonden</p>
                </div>
              )}
              <ul>
                {items.map((item, idx) => {
                  const isActive = idx === active;
                  if (item.kind === 'property') {
                    const p = item.property;
                    return (
                      <li key={`prop-${p.id}`}>
                        <button
                          type="button"
                          onClick={() => runItem(item)}
                          onMouseEnter={() => setActive(idx)}
                          className={`w-full px-6 py-3 flex items-center justify-between gap-3 text-left transition-colors ${
                            isActive ? 'bg-accent/5 border-l-2 border-accent' : 'border-l-2 border-transparent hover:bg-black/[0.03]'
                          }`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-lg bg-ink/5 flex items-center justify-center shrink-0">
                              <Home size={14} className="text-ink/60" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-ink truncate">
                                <Highlighted text={p.address} query={query} />
                              </p>
                              <p className="text-[11px] text-ink/50 truncate">
                                {[p.city, `${p.render_count} render${p.render_count === 1 ? '' : 's'}`, timeAgo(p.last_render_at)]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </p>
                            </div>
                          </div>
                          {isActive && (
                            <kbd className="hidden md:inline text-[10px] font-mono bg-ink/5 px-2 py-1 rounded shrink-0">↵</kbd>
                          )}
                        </button>
                      </li>
                    );
                  }
                  // "new" affordance
                  const hasMatches = matches.length > 0;
                  return (
                    <li key="new-affordance" className={hasMatches ? 'border-t border-black/8 mt-2' : ''}>
                      {hasMatches && (
                        <p className="px-6 pt-3 pb-2 text-[10.5px] font-semibold uppercase tracking-[0.11em] text-ink/45">Of</p>
                      )}
                      <button
                        type="button"
                        onClick={() => runItem(item)}
                        onMouseEnter={() => setActive(idx)}
                        disabled={creating}
                        className={`w-full px-6 py-4 flex items-center gap-3 text-left transition-colors ${
                          isActive ? 'bg-accent/5 border-l-2 border-accent' : 'border-l-2 border-transparent hover:bg-black/[0.03]'
                        } disabled:opacity-60 disabled:cursor-not-allowed`}
                      >
                        <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center text-accent shrink-0">
                          {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={18} strokeWidth={2.5} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-ink truncate">
                            {query.trim() ? (
                              <>Nieuw: <span className="text-accent">"{query.trim()}"</span></>
                            ) : (
                              'Nieuwe woning aanmaken'
                            )}
                          </p>
                          {query.trim() && (
                            <p className="text-[11px] text-ink/55 mt-0.5">Maak woning aan en ga direct naar upload</p>
                          )}
                        </div>
                        {isActive && !creating && (
                          <kbd className="hidden md:inline text-[10px] font-mono bg-white px-2 py-1 rounded border border-black/10 shrink-0">↵</kbd>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        {/* Footer (desktop only — keyboard hints) */}
        <div className="hidden md:flex px-6 py-3 bg-neutral-50 border-t border-black/8 items-center justify-between text-[11px] text-ink/55">
          <span>
            <kbd className="font-mono bg-white px-1.5 py-0.5 rounded border border-black/10">↑↓</kbd> navigeer ·{' '}
            <kbd className="font-mono bg-white px-1.5 py-0.5 rounded border border-black/10">↵</kbd> kies ·{' '}
            <kbd className="font-mono bg-white px-1.5 py-0.5 rounded border border-black/10">esc</kbd> sluit
          </span>
        </div>
      </div>
    </div>
  );
};
